import React, {useEffect, useMemo, useRef} from 'react';
import {Box, IconButton} from '@mui/material';
import {
  Body,
  Headline,
  Icon,
  designSystemToken,
} from '@lightricks/react-design-system';
import aggregateActivityFeedItems from '@/utils/aggregateActivityFeedItems';
import translate from '@/utils/translate';
import {
  ContentSubmission,
  ContentSubmissionVersion,
} from '@/types/contentSubmission';
import {getApprovalStepDisplayName} from '@/views/campaigns/constants/approvalSteps';
import useContentSubmissionOffPlatformReviewStore, {
  contentSubmissionOffPlatformReviewActions,
} from '@/stores/contentSubmissionOffPlatformReviewStore';
import sharedStyles from './ContentSubmissionActivityFeed.module.scss';
import FeedEmptyState from './FeedEmptyState';
import FeedItemView from './FeedItemView';
import styles from './OffPlatformActivityFeed.module.scss';
import OffPlatformReviewFooter from './OffPlatformReviewFooter';

interface OffPlatformActivityFeedProps {
  testID?: string;
  version: ContentSubmissionVersion;
  contentSubmission: ContentSubmission;
}

function OffPlatformActivityFeed({
  testID = 'content-submission-activity-feed',
  version,
  contentSubmission,
}: OffPlatformActivityFeedProps) {
  const userIdentity = useContentSubmissionOffPlatformReviewStore(
    (s) => s.userIdentity
  );
  const isEditingIdentity = useContentSubmissionOffPlatformReviewStore(
    (s) => s.isEditingIdentity
  );
  const {setIsEditingIdentity} = contentSubmissionOffPlatformReviewActions;

  const feedListRef = useRef<HTMLDivElement>(null);

  // The token-authed content-submission payload embeds all comments + approvals
  // across every version with no per-viewer filtering, so deriving the feed from
  // it here (as OnPlatformActivityFeed does) surfaces every reviewer's activity.
  const feedItems = useMemo(
    () => aggregateActivityFeedItems(contentSubmission),
    [contentSubmission]
  );

  // Chat-style ordering: items are sorted oldest -> newest, so we keep the
  // scroll position pinned to the bottom whenever the feed length changes.
  useEffect(() => {
    feedListRef.current?.scrollTo({
      top: feedListRef.current.scrollHeight,
    });
  }, [feedItems.length]);

  const hasApprovalSteps =
    (contentSubmission.campaign?.campaignApprovalSteps ?? []).length > 0;
  const showSubtitle = hasApprovalSteps
    ? !!userIdentity?.role
    : !!userIdentity?.name;
  const roleDisplayName = userIdentity?.role
    ? getApprovalStepDisplayName(userIdentity.role)
    : '';

  return (
    <Box className={sharedStyles.container} data-testid={testID}>
      <Box className={styles.header}>
        <Headline size="xs" className={sharedStyles.title}>
          {translate('Activity feed')}
        </Headline>
        {showSubtitle ? (
          <Box className={styles.headerSubtitle}>
            <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
              <Body component="span" size="sm" className={styles.identityName}>
                {userIdentity!.name}
              </Body>
              {roleDisplayName ? ` (${roleDisplayName})` : null}
            </Body>
            <IconButton
              size="small"
              onClick={() => setIsEditingIdentity(!isEditingIdentity)}
              aria-label="edit identity"
            >
              <Icon name="Navigation-Edit" size="small" appearance="neutral" />
            </IconButton>
          </Box>
        ) : null}
      </Box>

      <Box ref={feedListRef} className={sharedStyles.feedList}>
        {feedItems.length === 0 ? (
          <FeedEmptyState />
        ) : (
          feedItems.map((item) => <FeedItemView key={item.id} item={item} />)
        )}
      </Box>

      <OffPlatformReviewFooter
        contentSubmission={contentSubmission}
        version={version}
      />
    </Box>
  );
}

export default OffPlatformActivityFeed;
